/**
 * StrategyBuilder — the legs you picked from the chain, their payoff, margin,
 * and the button that sends every leg through the risk engine to the broker.
 */

import { useEffect, useMemo, useState } from 'react';
import { Bookmark, Minus, Plus, Send, Trash2, X } from 'lucide-react';
import { api, getErrorMessage } from '@/config/api';
import { cn } from '@/lib/cn';
import { formatINR, formatNumber, formatQty } from '@/lib/format';
import { analyzePosition, describeLegs, legQuantity } from '@/lib/options';
import type { StrategyLeg } from '@/lib/options';
import { Alert, Badge, Button, Field, Input, Modal, Select } from '@/components/ui';
import { PayoffChart } from './PayoffChart';

interface LegResult {
  side: string;
  option_type: string;
  strike: string;
  trading_symbol: string;
  quantity: number;
  order_id: string;
  status: string;
  broker_order_id: string;
  risk_rejected: boolean;
  violations: string[];
  message: string;
}

interface BasketResult {
  basket_id: string;
  name: string;
  legs: LegResult[];
  placed: number;
  rejected: number;
}

interface MarginResult {
  total_required: string;
  available_margin: string | null;
  is_sufficient: boolean | null;
  approximate: boolean;
  legs: Array<{ trading_symbol: string; side: string; required_margin: string; error: string }>;
}

interface StrategyBuilderProps {
  legs: StrategyLeg[];
  lotSize: number;
  spot: number | null;
  underlying: string;
  segment: string;
  expiry: string;
  connectionId: string | null;
  onChange: (legs: StrategyLeg[]) => void;
  onPlaced?: (result: BasketResult) => void;
  onSaved?: () => void;
}

export function StrategyBuilder({
  legs,
  lotSize,
  spot,
  underlying,
  segment,
  expiry,
  connectionId,
  onChange,
  onPlaced,
  onSaved,
}: StrategyBuilderProps) {
  const [product, setProduct] = useState<'NRML' | 'MIS'>('NRML');
  const [orderType, setOrderType] = useState<'MKT' | 'L'>('MKT');
  const [name, setName] = useState('');
  const [marginState, setMarginState] = useState<{ key: string; data: MarginResult | null; error: string | null } | null>(null);
  const [marginLoading, setMarginLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [result, setResult] = useState<BasketResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const summary = useMemo(() => analyzePosition(legs, lotSize, spot), [legs, lotSize, spot]);
  const autoName = useMemo(() => describeLegs(legs), [legs]);
  const legsKey = legs.map((l) => `${l.side}${l.option_type}${l.strike}x${l.lots}@${l.price}`).join('|');
  const marginKey = `${legsKey}|${product}|${connectionId ?? ''}|${lotSize}`;
  // A preview only counts when it was computed for the legs on screen right now.
  const margin = marginState?.key === marginKey ? marginState.data : null;
  const marginError = marginState?.key === marginKey ? marginState.error : null;

  // Margin preview, debounced on any leg change.
  useEffect(() => {
    if (legs.length === 0 || !connectionId) return;
    const key = marginKey;
    const handle = setTimeout(async () => {
      setMarginLoading(true);
      try {
        const { data } = await api.post('/options/strategies/margin', {
          connection_id: connectionId,
          product,
          legs: legs.map((l) => ({
            side: l.side,
            option_type: l.option_type,
            strike: l.strike,
            instrument_token: l.instrument_token,
            trading_symbol: l.trading_symbol,
            exchange_segment: l.exchange_segment,
            quantity: legQuantity(l, lotSize),
            price: l.price,
          })),
        });
        setMarginState({ key, data, error: null });
      } catch (err) {
        setMarginState({ key, data: null, error: getErrorMessage(err) });
      } finally {
        setMarginLoading(false);
      }
    }, 600);
    return () => clearTimeout(handle);
    // marginKey captures every leg attribute that affects margin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marginKey]);

  const update = (id: string, patch: Partial<StrategyLeg>) => onChange(legs.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const remove = (id: string) => onChange(legs.filter((l) => l.id !== id));

  const place = async () => {
    setPlacing(true);
    setError(null);
    try {
      const { data } = await api.post<BasketResult>('/options/strategies/execute', {
        name: name.trim() || autoName,
        product,
        order_type: orderType,
        connection_id: connectionId,
        legs: legs.map((l) => ({
          side: l.side,
          option_type: l.option_type,
          strike: l.strike,
          instrument_token: l.instrument_token,
          trading_symbol: l.trading_symbol,
          exchange_segment: l.exchange_segment,
          quantity: legQuantity(l, lotSize),
          price: l.price,
        })),
      });
      setResult(data);
      setConfirmOpen(false);
      onPlaced?.(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setPlacing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post('/options/strategies/saved', {
        name: name.trim() || autoName,
        underlying,
        segment,
        expiry,
        legs: legs.map((l) => ({ side: l.side, option_type: l.option_type, strike: l.strike, lots: l.lots })),
      });
      setSaveOpen(false);
      onSaved?.();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const credit = summary.netPremium >= 0;

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <Alert tone="danger" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result && (
        <Alert
          tone={result.rejected === 0 ? 'success' : 'warning'}
          title={`${result.name}: ${result.placed} of ${result.legs.length} legs sent`}
          onDismiss={() => setResult(null)}
        >
          <ul className="mt-1 flex flex-col gap-0.5 text-xs">
            {result.legs.map((leg) => (
              <li key={leg.order_id || leg.trading_symbol} className="font-tabular">
                {leg.side === 'B' ? 'BUY' : 'SELL'} {leg.trading_symbol} × {formatQty(leg.quantity)} · {leg.status}
                {leg.broker_order_id && ` · #${leg.broker_order_id}`}
                {leg.status !== 'sent' && leg.message && ` · ${leg.message}`}
                {leg.violations?.length > 0 && ` · ${leg.violations.join(', ')}`}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Legs */}
      {legs.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line px-3 py-6 text-center text-xs text-fg-subtle">
          Click <span className="font-semibold text-buy">B</span> or <span className="font-semibold text-sell">S</span> on any
          call or put in the chain, or pick a preset, to start building a strategy.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {legs.map((leg) => (
            <li key={leg.id} className="rounded-lg border border-line bg-surface-2 p-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => update(leg.id, { side: leg.side === 'B' ? 'S' : 'B' })}
                  className={cn(
                    'rounded-md px-2 py-0.5 text-xs font-semibold text-white',
                    leg.side === 'B' ? 'bg-buy' : 'bg-sell'
                  )}
                  aria-label={`Flip side for ${leg.trading_symbol}`}
                >
                  {leg.side === 'B' ? 'BUY' : 'SELL'}
                </button>
                <span className="font-tabular text-sm font-medium text-fg">{formatNumber(leg.strike)}</span>
                <Badge tone={leg.option_type === 'CE' ? 'success' : 'danger'}>{leg.option_type}</Badge>
                <span className="min-w-0 flex-1 truncate text-xs text-fg-subtle">{leg.trading_symbol}</span>
                <Button variant="ghost" size="icon" aria-label={`Remove ${leg.trading_symbol}`} onClick={() => remove(leg.id)}>
                  <X className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div className="flex items-center gap-1">
                  <Button variant="secondary" size="icon" aria-label="Fewer lots" onClick={() => update(leg.id, { lots: Math.max(1, leg.lots - 1) })}>
                    <Minus className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                  <div className="flex-1 text-center font-tabular text-xs text-fg-muted">
                    {leg.lots} lot{leg.lots === 1 ? '' : 's'} · {formatQty(legQuantity(leg, lotSize))}
                  </div>
                  <Button variant="secondary" size="icon" aria-label="More lots" onClick={() => update(leg.id, { lots: leg.lots + 1 })}>
                    <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </div>
                <label className="flex items-center gap-2 text-xs text-fg-subtle">
                  <span className="shrink-0">Premium</span>
                  <Input
                    type="number"
                    step="0.05"
                    min={0}
                    mono
                    className="h-8"
                    value={leg.price}
                    onChange={(e) => update(leg.id, { price: Number(e.target.value) || 0 })}
                    aria-label={`Premium for ${leg.trading_symbol}`}
                  />
                </label>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Payoff */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-xs font-medium tracking-wider text-fg-faint uppercase">Payoff at expiry</p>
          {legs.length > 0 && <span className="text-xs text-fg-subtle">{autoName}</span>}
        </div>
        <PayoffChart curve={summary.curve} spot={spot} breakevens={summary.breakevens} />
      </div>

      {legs.length > 0 && (
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg border border-line px-3 py-2">
            <dt className="text-xs text-fg-faint">{credit ? 'Net credit' : 'Net debit'}</dt>
            <dd className={cn('font-tabular font-medium', credit ? 'text-profit' : 'text-loss')}>
              {formatINR(Math.abs(summary.netPremium))}
            </dd>
          </div>
          <div className="rounded-lg border border-line px-3 py-2">
            <dt className="text-xs text-fg-faint">Breakeven{summary.breakevens.length === 1 ? '' : 's'}</dt>
            <dd className="font-tabular text-fg-muted">
              {summary.breakevens.length ? summary.breakevens.map((b) => formatNumber(b)).join(' / ') : '—'}
            </dd>
          </div>
          <div className="rounded-lg border border-line px-3 py-2">
            <dt className="text-xs text-fg-faint">Max profit</dt>
            <dd className="font-tabular font-medium text-profit">
              {summary.maxProfit === null ? 'Unlimited' : formatINR(summary.maxProfit)}
            </dd>
          </div>
          <div className="rounded-lg border border-line px-3 py-2">
            <dt className="text-xs text-fg-faint">Max loss</dt>
            <dd className="font-tabular font-medium text-loss">
              {summary.maxLoss === null ? 'Unlimited' : formatINR(Math.abs(summary.maxLoss))}
            </dd>
          </div>
          <div className="col-span-2 rounded-lg border border-line px-3 py-2">
            <dt className="text-xs text-fg-faint">Margin required (approx., sum of legs)</dt>
            <dd className="flex flex-wrap items-center gap-2 font-tabular text-fg-muted">
              {!connectionId
                ? 'Connect a broker to preview margin'
                : marginLoading
                  ? 'Calculating…'
                  : margin
                    ? (
                      <>
                        <span className="font-medium text-fg">{formatINR(margin.total_required)}</span>
                        {margin.available_margin && <span>· available {formatINR(margin.available_margin)}</span>}
                        {margin.is_sufficient !== null && (
                          <Badge tone={margin.is_sufficient ? 'success' : 'danger'}>
                            {margin.is_sufficient ? 'Sufficient' : 'Short of margin'}
                          </Badge>
                        )}
                      </>
                    )
                    : marginError
                      ? <span className="text-danger">{marginError}</span>
                      : '—'}
            </dd>
          </div>
        </dl>
      )}

      {/* Execution controls */}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Product">
          {(field) => (
            <Select {...field} value={product} onChange={(e) => setProduct(e.target.value as 'NRML' | 'MIS')}>
              <option value="NRML">NRML · carry forward</option>
              <option value="MIS">MIS · intraday</option>
            </Select>
          )}
        </Field>
        <Field label="Order type">
          {(field) => (
            <Select {...field} value={orderType} onChange={(e) => setOrderType(e.target.value as 'MKT' | 'L')}>
              <option value="MKT">Market</option>
              <option value="L">Limit at premium</option>
            </Select>
          )}
        </Field>
      </div>
      <Field label="Strategy name" hint={`Defaults to "${autoName}"; tags every leg in the order book.`}>
        {(field) => <Input {...field} value={name} onChange={(e) => setName(e.target.value)} placeholder={autoName} maxLength={64} />}
      </Field>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          icon={Send}
          disabled={legs.length === 0 || !connectionId}
          onClick={() => setConfirmOpen(true)}
          title={!connectionId ? 'Connect a broker in Settings first' : undefined}
        >
          Place {legs.length || ''} leg{legs.length === 1 ? '' : 's'}
        </Button>
        <Button variant="secondary" icon={Bookmark} disabled={legs.length === 0} onClick={() => setSaveOpen(true)}>
          Save
        </Button>
        <Button variant="ghost" icon={Trash2} disabled={legs.length === 0} onClick={() => onChange([])}>
          Clear
        </Button>
      </div>

      {/* Confirm */}
      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={`Place ${legs.length} leg${legs.length === 1 ? '' : 's'}?`}
        description="Buy legs go first, then sells. Each leg passes the risk engine before it reaches the broker."
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Back
            </Button>
            <Button variant="primary" icon={Send} loading={placing} onClick={place}>
              Confirm and place
            </Button>
          </>
        }
      >
        <ul className="divide-y divide-line rounded-lg border border-line">
          {[...legs].sort((a, b) => (a.side === b.side ? 0 : a.side === 'B' ? -1 : 1)).map((leg) => (
            <li key={leg.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="flex items-center gap-2">
                <span className={cn('text-xs font-semibold', leg.side === 'B' ? 'text-buy' : 'text-sell')}>
                  {leg.side === 'B' ? 'BUY' : 'SELL'}
                </span>
                <span className="font-tabular text-fg">{leg.trading_symbol}</span>
              </span>
              <span className="font-tabular text-xs text-fg-muted">
                {formatQty(legQuantity(leg, lotSize))} @ {orderType === 'L' ? formatINR(leg.price) : 'MKT'} · {product}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-fg-subtle">
          {summary.maxLoss === null ? 'This position has unlimited loss potential.' : `Max loss ${formatINR(Math.abs(summary.maxLoss))}.`}{' '}
          {margin ? `Approx. margin ${formatINR(margin.total_required)}.` : ''}
        </p>
      </Modal>

      {/* Save */}
      <Modal
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        title="Save strategy template"
        description="Saves the legs by strike and side; reload it on any later chain."
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setSaveOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" loading={saving} onClick={save}>
              Save
            </Button>
          </>
        }
      >
        <Field label="Name" required>
          {(field) => <Input {...field} value={name} onChange={(e) => setName(e.target.value)} placeholder={autoName} maxLength={64} />}
        </Field>
      </Modal>
    </div>
  );
}
